import { useState, useMemo, useRef } from "react";
import { ScrollToTop } from "./ScrollToTop";
import { usePageMeta } from "../utils/usePageMeta";

type Estat = "concept" | "serie";

interface Stat {
  label: string;
  value: string;
}

interface Novetat {
  id: string;
  brand: string;
  model: string;
  estat: Estat;
  propulsio: string;
  presentat: string;
  arribada: string;
  preu: string;
  stats: Stat[];
  info: string;
  images: string[];
  // Vídeo curt en bucle (primera diapositiva) i el vídeo complet, que s'obre en clicar-lo
  video?: { src: string; poster: string; complet: string };
  // Anunci oficial de la marca (newsroom); s'obre en clicar la foto
  anunci: string;
  font: string;
}

// Dades recollides el 15-16/10/2026 (Saló de París i presentacions de la mateixa setmana).
// Els concepts sovint no tenen xifres oficials: quan una dada és estimada, ho diu.
const novetats: Novetat[] = [
  {
    id: "tesla-roadster",
    brand: "Tesla",
    model: "Roadster",
    estat: "serie",
    propulsio: "Elèctric + propulsors d'aire comprimit",
    presentat: "15 oct 2026 · McGregor (Texas)",
    arribada: "2028, segons Musk",
    preu: "No anunciat · reserva de 50.000 $",
    stats: [
      { label: "0-97 km/h", value: "< 1,6 s (Rocket: < 0,59 s)" },
      { label: "Velocitat màx.", value: "> 370 km/h (Rocket)" },
      { label: "Autonomia", value: "> 480 km" },
      { label: "Propulsors", value: "10, amb 600 l d'aire comprimit" },
      { label: "Succió", value: "~2 G, prou per anar cap per avall" },
    ],
    info: "Nou anys després de l'anunci, Tesla l'ha presentat en una base de proves de SpaceX. Uns ventiladors xuclen el cotxe contra el terra amb una força que dobla el seu pes: així ha pujat una rampa corbada fins a quedar cap per avall. Llavors s'ha deixat anar, ha girat en l'aire amb propulsors d'aire comprimit fets amb SpaceX i ha aterrat sobre les rodes. També ha saltat per sobre d'un Semi, un Cybertruck i tres Tesla més. Hi haurà tres versions: Performance i Thrust, de dues places, i Rocket, d'una plaça i no homologada per circular.",
    images: [
      "/novetats/tesla-roadster.webp",
      "/novetats/tesla-roadster-2.webp",
      "/novetats/tesla-roadster-3.webp",
      "/novetats/tesla-roadster-4.webp",
      "/novetats/tesla-roadster-5.webp",
      "/novetats/tesla-roadster-6.webp",
      "/novetats/tesla-roadster-7.webp",
    ],
    video: {
      src: "/novetats/tesla-roadster.mp4",
      poster: "/novetats/tesla-roadster.webp",
      complet: "https://www.youtube.com/watch?v=qt5p-_tE8d0&t=645s",
    },
    anunci: "https://www.tesla.com/roadster",
    font: "https://electrek.co/2026/10/15/tesla-unveils-cyberfied-new-roadster-kickflip/",
  },
  {
    id: "porsche-mission-s",
    brand: "Porsche",
    model: "Mission S",
    estat: "concept",
    propulsio: "Combustió, motor central",
    presentat: "15 oct 2026 · Nova York",
    arribada: "Sense data (codi intern S1)",
    preu: "~500.000 € (estimació)",
    stats: [
      { label: "Motor", value: "Bòxer de 8 cil. (no confirmat)" },
      { label: "Règim màxim", value: "~8.000 rpm" },
      { label: "Velocímetre", value: "fins a 250 mph (~400 km/h)" },
    ],
    info: "El successor espiritual del 918 Spyder: un superesportiu de motor central per sobre del 911 que estrena el nou llenguatge de disseny de Porsche. Morro baix i fars rodons inspirats en el 550 Spyder, un ventilador a la tapa del motor com el del 917 i quatre escapaments en parelles.",
    images: [
      "/novetats/porsche-mission-s.webp",
      "/novetats/porsche-mission-s-2.webp",
      "/novetats/porsche-mission-s-3.webp",
      "/novetats/porsche-mission-s-4.webp",
      "/novetats/porsche-mission-s-5.webp",
      "/novetats/porsche-mission-s-6.webp",
    ],
    anunci: "https://newsroom.porsche.com/en/2026/products/porsche-mission-s-vision-model-43528.html",
    font: "https://www.thesupercarblog.com/porsche-mission-s-concept-unveiled-a-successor-to-the-918-spyder/",
  },
  {
    id: "peugeot-e-t16",
    brand: "Peugeot",
    model: "E-T16",
    estat: "concept",
    propulsio: "Elèctric, 3 motors",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Possible sèrie limitada, sense data",
    preu: "No anunciat",
    stats: [
      { label: "Potència", value: "~815 CV" },
      { label: "0-100 km/h", value: "2,5 s" },
      { label: "0-300 km/h", value: "16 s" },
      { label: "Bateria", value: "85 kWh · 800 V" },
      { label: "Autonomia", value: "~400 km" },
      { label: "Pes", value: "1.610 kg" },
    ],
    info: "Homenatge als 40 anys del 205 T16 que va dominar el Mundial de Ral·lis. És el Peugeot més potent de la història: tracció total amb vectorització de parell, aerodinàmica activa i el volant Hypersquare. Peugeot diu que en faria una sèrie curta si hi ha prou col·leccionistes interessats.",
    images: [
      "/novetats/peugeot-e-t16.webp",
      "/novetats/peugeot-e-t16-2.webp",
      "/novetats/peugeot-e-t16-3.webp",
      "/novetats/peugeot-e-t16-4.webp",
      "/novetats/peugeot-e-t16-5.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/peugeot/press/peugeot-e-t16-concept-an-icon-reinvented",
    font: "https://newatlas.com/automotive/peugeot-e-t16-concept-paris-motor-show-2026/",
  },
  {
    id: "peugeot-rcz-vision",
    brand: "Peugeot",
    model: "RCZ Vision",
    estat: "concept",
    propulsio: "Elèctric, bateria d'estat sòlid",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "No prevista",
    preu: "—",
    stats: [
      { label: "Potència", value: "500 CV" },
      { label: "Autonomia", value: "fins a 600 km" },
      { label: "Càrrega 10-80%", value: "~10 min" },
      { label: "Consum", value: "11,5 kWh/100 km" },
      { label: "Arquitectura", value: "1.000 V" },
    ],
    info: "Recupera l'esperit del coupé RCZ, amb el sostre de doble bombolla de l'original. Fa servir una bateria d'estat sòlid de només 110 mm de gruix (feta amb Factorial Energy), direcció i frens by-wire i un interior amb només dues pantalles petites. Marca la direcció de disseny de Peugeot dels propers anys.",
    images: [
      "/novetats/peugeot-rcz-vision.webp",
      "/novetats/peugeot-rcz-vision-2.webp",
      "/novetats/peugeot-rcz-vision-3.webp",
      "/novetats/peugeot-rcz-vision-4.webp",
      "/novetats/peugeot-rcz-vision-5.webp",
      "/novetats/peugeot-rcz-vision-6.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/peugeot/press/peugeot-rcz-vision-emotion-at-the-heart-of-the-future-of-peugeot",
    font: "https://www.arenaev.com/peugeot_unveils_two_new_concept_cars__a_sporty_coupe_and_an_allelectric_rally_car-news-6300.php",
  },
  {
    id: "citroen-2cv",
    brand: "Citroën",
    model: "2 CV",
    estat: "concept",
    propulsio: "Elèctric",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "2028 (versió de sèrie)",
    preu: "~15.000 € (objectiu)",
    stats: [
      { label: "Llargada", value: "3,76 m" },
      { label: "Pes", value: "< 1.000 kg" },
      { label: "Bateria", value: "~30 kWh LFP" },
    ],
    info: "Torna el 2 CV, i és més curt que l'original. Lleuger, senzill i fabricat a Europa, se situarà entre l'Ami i el C3. Té sostre de lona enrotllable, seients tubulars, tiradors de tela i la «Radioën», una ràdio que es desmunta i fa d'altaveu fora del cotxe.",
    images: [
      "/novetats/citroen-2cv.webp",
      "/novetats/citroen-2cv-2.webp",
      "/novetats/citroen-2cv-3.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/citroen/press/citroen-2-cv-concept-time-2-meet-again",
    font: "https://newatlas.com/automotive/citroen-2-cv-concept-paris-motor-show-2026/",
  },
  {
    id: "citroen-2cv-fourgonnette",
    brand: "Citroën",
    model: "2 CV Fourgonnette",
    estat: "concept",
    propulsio: "Elèctric",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Sense confirmar",
    preu: "—",
    stats: [
      { label: "Llargada", value: "< 4 m" },
      { label: "Dades tècniques", value: "No anunciades" },
    ],
    info: "La versió furgoneta del nou 2 CV, amb la caixa de cantonades arrodonides que s'uneix al sostre com la «Fourgonnette» de 1951-1978. Gris com l'original, sense calandra, i amb l'endoll on abans hi havia el tap del dipòsit.",
    images: [
      "/novetats/citroen-2cv-fourgonnette.webp",
      "/novetats/citroen-2cv-fourgonnette-2.webp",
      "/novetats/citroen-2cv-fourgonnette-3.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/citroen/press/2-cv-fourgonnette-concept-citroen-revives-the-iconic-silhouette-of-its-light-commercial-vehicle",
    font: "https://www.electrive.com/2026/10/15/citroen-reimagines-the-2cv-fourgonnette-as-a-bev/",
  },
  {
    id: "alfa-romeo-cuorerosso",
    brand: "Alfa Romeo",
    model: "Cuorerosso",
    estat: "concept",
    propulsio: "Híbrid, V6 + tracció Q4",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "No es fabricarà tal qual",
    preu: "—",
    stats: [
      { label: "Motor", value: "V6 2,9 biturbo híbrid" },
      { label: "Potència", value: "> 520 CV (sense xifra oficial)" },
      { label: "Llargada", value: "4,7 m" },
      { label: "Places", value: "4 · 4 portes" },
    ],
    info: "Un coupé de quatre portes, amb les del darrere d'obertura inversa, pintat en «Liquid Ink Red». Porta el V6 del Giulia Quadrifoglio electrificat, el Trilobo amb l'Scudetto, pilots rodons que recorden l'8C i un toc de Lancia Stratos a les aletes posteriors. Inspirarà els propers Alfa, inclosos SUV i un compacte.",
    images: [
      "/novetats/alfa-cuorerosso.webp",
      "/novetats/alfa-cuorerosso-2.webp",
      "/novetats/alfa-cuorerosso-3.webp",
      "/novetats/alfa-cuorerosso-4.webp",
      "/novetats/alfa-cuorerosso-5.webp",
      "/novetats/alfa-cuorerosso-6.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/alfa-romeo/press/world-premiere-of-alfa-romeo-cuorerosso",
    font: "https://www.motoringresearch.com/car-news/alfa-romeo-cuorerosso-concept-paris/",
  },
  {
    id: "alpine-a310-vision",
    brand: "Alpine",
    model: "A310 Vision",
    estat: "concept",
    propulsio: "Elèctric",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Exemplar únic",
    preu: "—",
    stats: [
      { label: "Potència", value: "~450 CV" },
      { label: "Llargada", value: "4,55 m" },
      { label: "Alçada", value: "1,18 m" },
      { label: "Amplada", value: "2,03 m" },
      { label: "Arquitectura", value: "800 V" },
    ],
    info: "Reinterpretació retrofuturista de l'A310, el rival francès del 911. Fars escamotejables, aleró partit, carrosseria d'alumini i un habitacle semitransparent. Estudi de disseny sobre la plataforma APP d'Alpine, no un model de producció.",
    images: [
      "/novetats/alpine-a310-vision.webp",
      "/novetats/alpine-a310-vision-2.webp",
      "/novetats/alpine-a310-vision-3.webp",
      "/novetats/alpine-a310-vision-4.webp",
      "/novetats/alpine-a310-vision-5.webp",
    ],
    anunci: "https://media.alpinecars.com/alpine-presents-a310-vision-a-concept-inspired-by-one-of-its-major-icons-the-alpine-a310/?lang=eng",
    font: "https://lesalpinistes.com/en/alpine-a310-vision-concept-paris-motor-show-2026/",
  },
  {
    id: "ds-f3ver",
    brand: "DS",
    model: "F3VER",
    estat: "concept",
    propulsio: "Elèctric",
    presentat: "11 oct 2026 · Saló de París",
    arribada: "Sense data",
    preu: "—",
    stats: [
      { label: "Llargada", value: "4,19 m" },
      { label: "Batalla", value: "2,70 m" },
      { label: "Maleter", value: "400 L" },
      { label: "Autonomia", value: "«De les millors» (sense xifra)" },
    ],
    info: "Anticipa el nou disseny de DS: logotip vertical, llums LED en forma de Y i pilots posteriors amb 16 panells sobre una estructura de magnesi inspirada en la coberta del Grand Palais. El color Cosmic Blue canvia de blau a violeta segons la llum.",
    images: [
      "/novetats/ds-f3ver.webp",
      "/novetats/ds-f3ver-2.webp",
      "/novetats/ds-f3ver-3.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/ds/press/ds-f3ver-concept-a-new-design-expression-by-ds-automobiles",
    font: "https://www.carscoops.com/2026/10/the-future-of-ds-looks-very-bright-thanks-to-the-f3ver-concept/",
  },
  {
    id: "opel-stx",
    brand: "Opel",
    model: "STX",
    estat: "concept",
    propulsio: "Elèctric, tracció total",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Sense data",
    preu: "—",
    stats: [
      { label: "Vel. màx.", value: "> 200 km/h" },
      { label: "Llargada", value: "~4,70 m" },
      { label: "Batalla", value: "~3 m" },
      { label: "Pneumàtics", value: "285/35 R22" },
    ],
    info: "STX vol dir Sports Tourer Experimental: el familiar elèctric que imagina Opel. Evoluciona el frontal Vizor amb el Blitz il·luminat, i té aerodinàmica activa al darrere amb difusors blaus.",
    images: [
      "/novetats/opel-stx.webp",
      "/novetats/opel-stx-2.webp",
      "/novetats/opel-stx-3.webp",
      "/novetats/opel-stx-4.webp",
      "/novetats/opel-stx-5.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/opel/press/opel-stx-concept-visionary-study-from-ruesselsheim-shows-the-estate-design-of-tomorrow",
    font: "https://www.motor1.com/news/811442/opel-stx-concept-preview/",
  },
  {
    id: "renault-e-space",
    brand: "Renault",
    model: "E-Space",
    estat: "concept",
    propulsio: "Elèctric",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Sense data",
    preu: "—",
    stats: [
      { label: "Llargada", value: "4,7 m" },
      { label: "Places", value: "6 seients individuals" },
      { label: "Carrosseria", value: "Monovolum" },
    ],
    info: "El monovolum torna: una relectura de l'Espace de 1984 pensada com un espai per viure-hi. Portes posteriors d'obertura inversa sense pilar central, terra completament pla i seients que giren.",
    images: [
      "/novetats/renault-e-space.webp",
      "/novetats/renault-e-space-2.webp",
      "/novetats/renault-e-space-3.webp",
      "/novetats/renault-e-space-4.webp",
      "/novetats/renault-e-space-5.webp",
    ],
    anunci: "https://media.renault.com/renault-e-space-concept-a-contemporary-vision-of-voitures-a-vivre/?lang=eng",
    font: "https://newatlas.com/automotive/renault-e-space-concept-paris-motor-show-2026/",
  },
  {
    id: "fiat-multiplay",
    brand: "Fiat",
    model: "Multiplay",
    estat: "concept",
    propulsio: "No anunciada",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Sense data",
    preu: "—",
    stats: [
      { label: "Llargada", value: "4,45 m" },
      { label: "Places", value: "6+1 o 4 (pick-up)" },
    ],
    info: "Monovolum, SUV i pick-up alhora, en la línia del Multipla. El mòdul posterior llisca sobre guies (el pilar C «Metamorfosi») i el converteix en un pick-up de quatre places amb caixa oberta. El seient «Petsenger» serveix per a una cadireta, la mascota o com a tauleta.",
    images: [
      "/novetats/fiat-multiplay.webp",
      "/novetats/fiat-multiplay-2.webp",
      "/novetats/fiat-multiplay-3.webp",
      "/novetats/fiat-multiplay-4.webp",
      "/novetats/fiat-multiplay-5.webp",
      "/novetats/fiat-multiplay-6.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/fiat/press/fiat-multiplay-makes-his-debuts-in-paris-3-in-1-vehicle",
    font: "https://www.carscoops.com/2026/10/fiat-multiplay-concept-pickup-suv-minivan/",
  },
  {
    id: "smart-2",
    brand: "Smart",
    model: "#2",
    estat: "serie",
    propulsio: "Elèctric, motor posterior",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Lliuraments el 2027 (ja es pot reservar)",
    preu: "19.990-25.690 €",
    stats: [
      { label: "Potència", value: "82 CV (60 kW)" },
      { label: "Bateria", value: "35,8 kWh LFP" },
      { label: "Autonomia", value: "290 km WLTP" },
      { label: "Places", value: "2" },
    ],
    info: "Torna el fortwo, ara elèctric i de Mercedes i Geely. Quatre acabats: Pure (19.990 €), Pure+ (20.990 €), Premium (24.490 €) i la Couture Edition (25.690 €), limitada a 999 unitats a Europa.",
    images: [
      "/novetats/smart-2.webp",
      "/novetats/smart-2-2.webp",
      "/novetats/smart-2-3.webp",
      "/novetats/smart-2-4.webp",
      "/novetats/smart-2-5.webp",
    ],
    anunci: "https://media.smart.com/en-INT/273151-it-s-back-that-s-big-the-all-electric-smart-2-makes-its-world-premiere-at-the-paris-motor-show/",
    font: "https://www.electrive.com/2026/10/13/smart-2-now-available-to-pre-order-for-under-e20000/",
  },
  {
    id: "cupra-raval",
    brand: "Cupra",
    model: "Raval",
    estat: "serie",
    propulsio: "Elèctric (MEB+)",
    presentat: "2026 · Fet a Martorell",
    arribada: "2026",
    preu: "25.995-42.000 €",
    stats: [
      { label: "Potència", value: "116-226 CV" },
      { label: "Bateria", value: "37 o 52 kWh" },
      { label: "Autonomia", value: "fins a 450 km WLTP" },
      { label: "Vel. màx.", value: "175 km/h" },
      { label: "Llargada", value: "4,05 m" },
    ],
    info: "El petit elèctric de Cupra, fet a Martorell al costat del VW ID. Polo, i amb nom de barri de Barcelona. A Espanya: Raval 25.995 €, Plus 30.150 €, Endurance 36.100 € i VZ 42.000 €. A París s'hi va ensenyar una versió preparada per ABT.",
    images: [
      "/novetats/cupra-raval.webp",
      "/novetats/cupra-raval-2.webp",
      "/novetats/cupra-raval-3.webp",
      "/novetats/cupra-raval-4.webp",
      "/novetats/cupra-raval-5.webp",
    ],
    anunci: "https://www.seat-cupra-mediacenter.com/CUPRA-Brand/presskits/cupra-raval/press-release",
    font: "https://www.go-electra.com/en/newsroom/cupra-raval-electric-city-car-2026/",
  },
  {
    id: "leapmotor-b03",
    brand: "Leapmotor",
    model: "B03",
    estat: "serie",
    propulsio: "Elèctric",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "2027",
    preu: "~20.000-25.000 € (estimació)",
    stats: [
      { label: "Potència", value: "194 CV" },
      { label: "Bateria", value: "53 kWh LFP" },
      { label: "Autonomia", value: "390 km" },
      { label: "Llargada", value: "4,18 m" },
    ],
    info: "Compacte elèctric xinès que apunta directament al Renault 5 i al VW ID. Polo. Arribarà a 36 països europeus gràcies a l'aliança amb Stellantis, al costat del SUV B03X.",
    images: [
      "/novetats/leapmotor-b03.webp",
      "/novetats/leapmotor-b03-2.webp",
      "/novetats/leapmotor-b03-3.webp",
      "/novetats/leapmotor-b03-4.webp",
      "/novetats/leapmotor-b03-5.webp",
    ],
    anunci: "https://www.media.stellantis.com/em-en/leapmotor/press/leapmotor-accelerates-european-growth-at-the-2026-paris-motor-show-with-new-products-advanced-technology-and-local-production",
    font: "https://www.carscoops.com/2026/10/leapmotors-fast-affordable-b03-lands-in-europe-and-vw-and-renault-should-be-worried/",
  },
  {
    id: "bmw-serie-3",
    brand: "BMW",
    model: "Sèrie 3",
    estat: "serie",
    propulsio: "Gasolina, híbrid lleuger 48 V",
    presentat: "30 set 2026",
    arribada: "2027 (endollable també el 2027)",
    preu: "des de 49.100 €",
    stats: [
      { label: "318", value: "156 CV" },
      { label: "320", value: "211 CV · 0-100 en 7,4 s" },
      { label: "M350 xDrive", value: "443 CV · 0-100 en 4,1 s" },
      { label: "Pantalla", value: "17,9\" + projecció al parabrisa" },
      { label: "Maleter", value: "450 l" },
    ],
    info: "La vuitena generació, i el canvi més gran de la història del Sèrie 3: per fora és gairebé idèntic a l'i3 elèctric de la Neue Klasse, amb els ronyons units als fars. Ja no hi ha dièsel ni canvi manual, i desapareix la roda de l'iDrive. Es farà a Dingolfing; l'i3 elèctric, a Munic.",
    images: [
      "/novetats/bmw-serie-3.webp",
      "/novetats/bmw-serie-3-2.webp",
      "/novetats/bmw-serie-3-3.webp",
      "/novetats/bmw-serie-3-4.webp",
      "/novetats/bmw-serie-3-5.webp",
      "/novetats/bmw-serie-3-6.webp",
      "/novetats/bmw-serie-3-7.webp",
    ],
    anunci: "https://www.press.bmwgroup.com/global/article/detail/T0460993EN/the-new-bmw-3-series?language=en",
    font: "https://www.diariomotor.com/noticia/precio-bmw-serie-3-2027/",
  },
  {
    id: "jaguar-type-01",
    brand: "Jaguar",
    model: "Type 01",
    estat: "serie",
    propulsio: "Elèctric, 3 motors",
    presentat: "6 oct 2026 · Nova York",
    arribada: "Comandes a principis del 2027, entregues a finals",
    preu: "~150.000 € (130.000 £ al Regne Unit)",
    stats: [
      { label: "Potència", value: "> 1.000 CV" },
      { label: "0-100 km/h", value: "3,2 s" },
      { label: "Bateria", value: "118 kWh · 850 V" },
      { label: "Autonomia", value: "~700 km (estimació)" },
      { label: "Càrrega", value: "320 km en 13 min" },
    ],
    info: "El primer Jaguar després del polèmic canvi de marca del 2024, i el més potent de la seva història. És un gran turisme de quatre portes més llarg i baix que un Panamera, amb un morro molt llarg, portes sense marc i una càmera on hi hauria d'haver el vidre del darrere. Es dissenya i es fabrica al Regne Unit, i costa el doble del que costava un Jaguar de mitjana.",
    images: [
      "/novetats/jaguar-type-01.webp",
      "/novetats/jaguar-type-01-2.webp",
      "/novetats/jaguar-type-01-3.webp",
      "/novetats/jaguar-type-01-4.webp",
      "/novetats/jaguar-type-01-5.webp",
    ],
    anunci: "https://media.jlr.com/jaguar/news/2026/10/jaguar-type-01-signals-new-era-new-york-world-premiere",
    font: "https://www.hagerty.com/media/news/jaguars-controversial-production-ready-type-01-revealed/",
  },
  {
    id: "hyundai-tucson",
    brand: "Hyundai",
    model: "Tucson",
    estat: "serie",
    propulsio: "Gasolina, híbrid i endollable",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Primera meitat del 2027",
    preu: "No anunciat (l'actual, des de 34.400 €)",
    stats: [
      { label: "Motor", value: "1.6 T-GDI · 150 CV" },
      { label: "Híbrid", value: "245 CV" },
      { label: "Endollable", value: "292 CV · 81 km elèctrics" },
      { label: "Maleter", value: "695 l" },
      { label: "Pantalla", value: "17\"" },
    ],
    info: "La cinquena generació d'un dels SUV més venuts d'Espanya, amb un disseny quadrat de tot terreny que costa de reconèixer. És 3,5 cm més llarg i més ample, i el maleter creix 75 litres. Ja no té dièsel, porta el nou sistema Pleos Connect amb l'assistent de veu Gleo AI i la versió europea es fabricarà a Europa.",
    images: [
      "/novetats/hyundai-tucson.webp",
      "/novetats/hyundai-tucson-2.webp",
      "/novetats/hyundai-tucson-3.webp",
      "/novetats/hyundai-tucson-4.webp",
      "/novetats/hyundai-tucson-5.webp",
      "/novetats/hyundai-tucson-6.webp",
    ],
    anunci: "https://www.hyundai.news/eu/articles/press-releases/all-new-hyundai-tucson-october-2026.html",
    font: "https://www.motor.es/noticias/hyundai-tucson-2027-europa-2026116766.html",
  },
  {
    id: "byd-atto-8",
    brand: "BYD",
    model: "Atto 8",
    estat: "serie",
    propulsio: "Elèctric, tracció posterior o total",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Primer trimestre del 2027",
    preu: "No anunciat (> 70.000 € previst)",
    stats: [
      { label: "Potència", value: "fins a 775 CV" },
      { label: "0-100 km/h", value: "4,6 s (AWD)" },
      { label: "Bateria", value: "115 kWh" },
      { label: "Autonomia", value: "653 km WLTP" },
      { label: "Càrrega 10-97%", value: "9 min" },
      { label: "Places", value: "7 · 5,04 m" },
    ],
    info: "El SUV insígnia de BYD a Europa i el primer amb la càrrega «Flash»: del 10 al 70% en cinc minuts. Per aprofitar-la, BYD vol instal·lar 3.000 carregadors a Europa abans del 2028, 300 d'ells a Espanya. Té set places i un maleter davanter de 136 litres.",
    images: [
      "/novetats/byd-atto-8.webp",
      "/novetats/byd-atto-8-2.webp",
      "/novetats/byd-atto-8-3.webp",
      "/novetats/byd-atto-8-4.webp",
    ],
    anunci: "https://www.byd.com/de/presse/byd-atto-8-paris-motor-show-2026",
    font: "https://www.somoselectricos.com/coches-electricos/byd-atto-8-suv-electrico-7-plazas-que-carga-9-minutos-llega-espana-2027/20261013082045064299.html",
  },
  {
    id: "kia-seltos",
    brand: "Kia",
    model: "Seltos Hybrid",
    estat: "serie",
    propulsio: "Híbrid, davantera o e-AWD",
    presentat: "12 oct 2026 · Saló de París",
    arribada: "Gasolina, ja a la venda; híbrid, aviat",
    preu: "des de 28.030 € (gasolina)",
    stats: [
      { label: "Híbrid", value: "154 CV" },
      { label: "Híbrid e-AWD", value: "178 CV" },
      { label: "Maleter", value: "536 l" },
      { label: "V2L", value: "Endolla per alimentar aparells" },
    ],
    info: "La segona generació del Seltos, el segon SUV més venut del món, ja es ven a Espanya amb motor de gasolina. A París s'ha estrenat la versió híbrida, que és el primer Kia amb tracció total elèctrica e-AWD (un motor elèctric a l'eix del darrere) i el primer híbrid de la marca que pot donar corrent a aparells de fora.",
    images: [
      "/novetats/kia-seltos.webp",
      "/novetats/kia-seltos-2.webp",
      "/novetats/kia-seltos-3.webp",
      "/novetats/kia-seltos-4.webp",
      "/novetats/kia-seltos-5.webp",
    ],
    anunci: "https://www.hyundaimotorgroup.com/en/news/the-all-new-kia-seltos-hybrid-makes-its-european-premiere-at-2026-paris-motor-show",
    font: "https://www.carwow.es/kia/seltos/noticias/7799/segundo-suv-mas-vendido-precio-lanzamiento-medidas",
  },
];

interface Punt {
  titol: string;
  text: string;
}

interface Explicacio {
  id: string;
  pregunta: string;
  punts: Punt[];
  fonts: { nom: string; url: string }[];
}

const explicacions: Explicacio[] = [
  {
    id: "per-que-tants-cotxes",
    pregunta: "Per què ara surten tants cotxes?",
    punts: [
      {
        titol: "És el Saló de París.",
        text: "Es fa cada dos anys, i aquest any ha estat del 12 al 18 d'octubre. Les marques hi guarden les estrenes, i per això n'han sortit una dotzena en una sola setmana.",
      },
      {
        titol: "Les normes de CO₂ de la UE.",
        text: "Cada fabricant ha de complir una mitjana d'emissions entre el 2025 i el 2027, que es revisa a finals del 2027. Han de vendre més elèctrics, i el 2026 l'oferta de models elèctrics ha crescut un 50% en un sol any.",
      },
      {
        titol: "Arriben els elèctrics barats.",
        text: "Les vendes de models de menys de 25.000 € es multipliquen per set respecte al 2024. D'aquí surten el 2 CV de 15.000 €, el Smart #2 per menys de 20.000 € o el Leapmotor B03.",
      },
      {
        titol: "La competència xinesa.",
        text: "Les marques europees responen jugant la carta que les noves no tenen, la història. Per això tants concepts recuperen icones: el 2 CV, el 205 T16, el RCZ, l'A310 o l'Espace.",
      },
    ],
    fonts: [
      { nom: "Mondial de l'Auto", url: "https://mondial.paris/le-salon/mondial-de-lauto-2026" },
      { nom: "Comissió Europea", url: "https://climate.ec.europa.eu/areas-action/transport-decarbonisation/road-transport/cars-and-vans_en" },
      { nom: "T&E", url: "https://www.transportenvironment.org/articles/wave-of-affordable-electric-cars-is-boosting-consumers-choice-sales-of-sub-eur25-000-models-set-to-rise-sevenfold" },
    ],
  },
  {
    id: "per-que-tants-anuncis",
    pregunta: "I per què hi ha tants anuncis de cotxes a la tele?",
    punts: [
      {
        titol: "Les marques xineses.",
        text: "A Espanya ja n'hi ha més de 25. El 2022 tenien el 2,3% del mercat, i el 2026 ja en tenen al voltant del 13%, amb un rècord del 16,65% al juliol. Una marca que ningú coneix s'ha de fer conèixer de pressa, i la tele és la manera més ràpida d'arribar a tothom.",
      },
      {
        titol: "Les de sempre responen.",
        text: "Per no perdre clients, les marques tradicionals també anuncien més, sobretot els elèctrics nous que necessiten vendre per complir les normes de CO₂.",
      },
      {
        titol: "La tele continua manant.",
        text: "És el primer mitjà publicitari d'Espanya: el primer semestre del 2026 s'hi van invertir 916,7 milions d'euros, el 30% de tota la publicitat.",
      },
      {
        titol: "És temporada de llançaments.",
        text: "A la tardor arriben als concessionaris els models que s'acaben de presentar, i les marques hi concentren les campanyes.",
      },
    ],
    fonts: [
      { nom: "Motor16", url: "https://www.motor16.com/las-ultimas-noticias/marcas-chinas-espana-2026-cuota/" },
      { nom: "Autonocion", url: "https://www.autonocion.com/marcas-chinas-cuota-record-julio-2026/" },
      { nom: "Dircomfidencial", url: "https://dircomfidencial.com/marketing/las-marcas-chinas-impulsaran-la-inversion-publicitaria-en-espana-segun-wpp-media-20251210-0404/" },
    ],
  },
];

const estatLabel: Record<Estat, string> = {
  concept: "Concept",
  serie: "De sèrie",
};

const estatColor: Record<Estat, string> = {
  concept: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  serie: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
};

type Filtre = "tots" | Estat;

const Fila = ({ label, value, bold }: { label: string; value: string; bold?: boolean }) => (
  <div className="flex justify-between items-start gap-4">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className={`${bold ? "font-bold" : "font-semibold"} text-foreground text-right`}>{value}</span>
  </div>
);

const Detalls = ({ car }: { car: Novetat }) => (
  <div className="space-y-2.5 text-sm">
    <Fila label="Propulsió" value={car.propulsio} />
    {car.stats.map((s) => (
      <Fila key={s.label} label={s.label} value={s.value} />
    ))}
    <div className="pt-2 mt-2 border-t border-border space-y-2.5">
      <Fila label="Presentat" value={car.presentat} />
      <Fila label="Arribada" value={car.arribada} />
      <Fila label="Preu" value={car.preu} bold />
    </div>
    <div className="pt-2 mt-2 border-t border-dashed border-border">
      <p className="text-xs text-muted-foreground leading-relaxed">{car.info}</p>
      <a
        href={car.font}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Font
      </a>
    </div>
  </div>
);

const Badge = ({ estat }: { estat: Estat }) => (
  <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${estatColor[estat]}`}>
    {estatLabel[estat]}
  </span>
);

const Fletxa = ({ dir }: { dir: "prev" | "next" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points={dir === "prev" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}></polyline>
  </svg>
);

// Carrusel amb scroll-snap: es llisca amb el dit al mòbil i amb fletxes a l'ordinador.
// Clicar una foto obre l'anunci oficial de la marca.
const Carrusel = ({ car, className, lazy = true }: { car: Novetat; className: string; lazy?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [actual, setActual] = useState(0);
  const video = car.video;
  const total = car.images.length + (video ? 1 : 0);
  const esVideo = !!video && actual === 0;

  const vesA = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const n = (i + total) % total;
    el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = ref.current;
    if (el) setActual(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div className={`group/car relative bg-gradient-to-br from-secondary to-muted ${className}`}>
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex h-full w-full overflow-x-auto snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {video && (
          <a
            href={video.complet}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-full w-full shrink-0 snap-center bg-black"
            aria-label={`Vídeo de la presentació del ${car.brand} ${car.model}`}
          >
            <video
              src={video.src}
              poster={video.poster}
              className="h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload={lazy ? "metadata" : "auto"}
            />
          </a>
        )}
        {car.images.map((src, i) => (
          <a
            key={src}
            href={car.anunci}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-full w-full shrink-0 snap-center"
            aria-label={`Anunci oficial del ${car.brand} ${car.model}`}
          >
            <img
              src={src}
              alt={`${car.brand} ${car.model}, foto ${i + 1} de ${car.images.length}`}
              className="h-full w-full object-cover"
              loading={lazy || i > 0 || video ? "lazy" : undefined}
              draggable={false}
            />
          </a>
        ))}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => vesA(actual - 1)}
            className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-background/85 text-foreground shadow-sm backdrop-blur opacity-0 group-hover/car:opacity-100 focus-visible:opacity-100 transition-opacity"
            aria-label="Foto anterior"
          >
            <Fletxa dir="prev" />
          </button>
          <button
            type="button"
            onClick={() => vesA(actual + 1)}
            className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-background/85 text-foreground shadow-sm backdrop-blur opacity-0 group-hover/car:opacity-100 focus-visible:opacity-100 transition-opacity"
            aria-label="Foto següent"
          >
            <Fletxa dir="next" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
            {Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full bg-white shadow transition-all ${i === actual ? "w-4 opacity-100" : "w-1.5 opacity-60"}`}
              />
            ))}
          </div>
        </>
      )}

      <span className="absolute bottom-3 left-3 pointer-events-none inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
        {esVideo ? "Vídeo sencer ↗" : "Anunci ↗"}
      </span>
    </div>
  );
};

export const Novetats = () => {
  usePageMeta({
    title: "Novetats | Cotx.es",
    description: "Els cotxes nous i concepts anunciats recentment: fotos, dades, preus i quan arriben.",
  });

  const [filtre, setFiltre] = useState<Filtre>("tots");

  const [destacat, ...resta] = novetats;

  const filtrats = useMemo(
    () => (filtre === "tots" ? resta : resta.filter((c) => c.estat === filtre)),
    [filtre]
  );
  const mostraDestacat = filtre === "tots" || filtre === destacat.estat;

  const botons: { id: Filtre; label: string }[] = [
    { id: "tots", label: "Tots" },
    { id: "concept", label: "Concepts" },
    { id: "serie", label: "De sèrie" },
  ];

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Novetats</h1>
              <p className="text-muted-foreground mt-2">
                {novetats.length} cotxes anunciats recentment
              </p>
              <a
                href="#per-que-tants-cotxes"
                className="inline-block mt-2 text-sm font-medium text-foreground underline underline-offset-4 decoration-border hover:decoration-foreground"
              >
                Per què ara surten tants cotxes (i tants anuncis)?
              </a>
            </div>
            <div className="flex items-center gap-2">
              {botons.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setFiltre(b.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtre === b.id
                      ? "bg-foreground text-background"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {/* Destacat */}
          {mostraDestacat && (
            <div className="relative mb-5 bg-card border border-border rounded-2xl overflow-hidden hover:border-foreground hover:shadow-lg transition-all duration-300 lg:grid lg:grid-cols-5">
              <Carrusel car={destacat} lazy={false} className="w-full h-56 sm:h-80 lg:h-full lg:min-h-[26rem] lg:col-span-3" />
              <div className="absolute top-4 right-4 lg:right-auto lg:left-4 z-10">
                <Badge estat={destacat.estat} />
              </div>
              <div className="p-5 sm:p-6 lg:col-span-2">
                <div className="mb-4 pb-3 border-b border-border">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    La més recent
                  </p>
                  <h2 className="text-2xl font-bold text-foreground leading-tight">
                    {destacat.brand} {destacat.model}
                  </h2>
                </div>
                <Detalls car={destacat} />
              </div>
            </div>
          )}

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtrats.map((car) => (
              <div
                key={car.id}
                className="relative bg-card border border-border rounded-2xl overflow-hidden hover:border-foreground hover:shadow-lg transition-all duration-300"
              >
                <Carrusel car={car} className="w-full h-48" />
                <div className="absolute top-4 right-4 z-10">
                  <Badge estat={car.estat} />
                </div>
                <div className="p-5">
                  <div className="mb-4 pb-3 border-b border-border">
                    <h3 className="text-lg font-bold text-foreground leading-tight">
                      {car.brand} {car.model}
                    </h3>
                  </div>
                  <Detalls car={car} />
                </div>
              </div>
            ))}
          </div>

          {/* Explicació */}
          <div className="mt-12 pt-10 border-t border-border grid grid-cols-1 lg:grid-cols-2 gap-5">
            {explicacions.map((e) => (
              <section
                key={e.id}
                id={e.id}
                className="scroll-mt-24 bg-card border border-border rounded-2xl p-5 sm:p-6"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-foreground leading-tight mb-5">
                  {e.pregunta}
                </h2>
                <ol className="space-y-4">
                  {e.punts.map((pt, i) => (
                    <li key={pt.titol} className="flex gap-3">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-secondary text-foreground text-xs font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        <span className="font-semibold text-foreground">{pt.titol}</span> {pt.text}
                      </p>
                    </li>
                  ))}
                </ol>
                <p className="mt-5 pt-4 border-t border-dashed border-border text-xs text-muted-foreground">
                  Fonts:{" "}
                  {e.fonts.map((f, i) => (
                    <span key={f.url}>
                      {i > 0 && " · "}
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                      >
                        {f.nom}
                      </a>
                    </span>
                  ))}
                </p>
              </section>
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-muted-foreground">
            Actualitzat el 16 d'octubre de 2026 · Clica una foto per veure l'anunci oficial de la marca.
          </p>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default Novetats;
